import { EVERY_DAY } from "@gigsync/protocol";
import { CardKind } from "../experts.js";
import { bookedTimes } from "../job/taken.js";
import { clashes } from "../job/terms.js";
import { log } from "../log.js";
import { Collection } from "../ports.js";
import { readSchedule, ScheduleAction } from "./read.js";
import { isOn, listBookings, removeBooking, saveBooking, upcoming } from "./store.js";
import { bookingWhens } from "./windows.js";
/**
 * The agent's answer to a message about the worker's schedule (ADR 0048): keep a booking, list what is coming, or take
 * one off. The words were read by code (`readSchedule`); the card carries the bookings and signed jobs themselves, so
 * the phone writes their days and times in whatever language the owner reads later.
 */
const schedule = (key, bookings = [], jobs = []) => ({
    kind: CardKind.schedule,
    say: { key },
    bookings,
    jobs,
});
/** Two stretches of a day share some minutes, on a day both fall on. */
const overlaps = (a, b) => (a.day === b.day || a.day === EVERY_DAY || b.day === EVERY_DAY) && a.start_min < b.end_min && b.start_min < a.end_min;
/** What a new booking runs into: other bookings by their minutes, signed jobs by their part of the day. */
function clashesWith(booking, bookings, jobs) {
    const mine = bookingWhens([booking]);
    return {
        bookings: bookings.filter((b) => overlaps(booking, b)),
        jobs: jobs.filter((j) => mine.some((w) => clashes(w, j))),
    };
}
/** Signed jobs still to come, on `day` when one was said. */
const jobsAhead = (jobs, now, day) => jobs.filter((j) => (j.day === EVERY_DAY || j.day >= now.day) && (!day || j.day === day || j.day === EVERY_DAY));
/** Keeps the booking, saying so and naming what it runs into; a save always has a day and hours (`readSchedule`). */
async function save(storage, ask, at, now) {
    const { day, start_min, end_min, note } = ask;
    if (!day || start_min === undefined || end_min === undefined)
        return list(storage, ask, now);
    const booking = { day, start_min, end_min, ...(note ? { note } : {}) };
    const [all, jobs] = await Promise.all([listBookings(storage), signedJobs(storage)]);
    const clash = clashesWith(booking, all, jobs);
    const saved = await saveBooking(storage, booking, at, now);
    const any = clash.bookings.length + clash.jobs.length > 0;
    return schedule(any ? "schedule.saved_clash" : "schedule.saved", [saved, ...clash.bookings], clash.jobs);
}
/** What is coming, on the day said or from now on, with the signed jobs beside the bookings. */
async function list(storage, ask, now) {
    const bookings = upcoming(await listBookings(storage), now).filter((b) => !ask.day || isOn(b, ask.day));
    const jobs = jobsAhead(await signedJobs(storage), now, ask.day);
    if (bookings.length + jobs.length > 0)
        return schedule("schedule.list", bookings, jobs);
    return schedule(ask.day ? "schedule.free_day" : "schedule.empty");
}
/** Takes off the one booking the words point at; several that fit are listed so the owner can say which. */
async function cancel(storage, ask, now) {
    const { day, start_min, end_min } = ask;
    const fits = upcoming(await listBookings(storage), now).filter((b) => (!day || isOn(b, day)) &&
        (start_min === undefined || end_min === undefined || overlaps(b, { day: b.day, start_min, end_min })));
    const [only] = fits;
    if (!only)
        return schedule("schedule.none_to_cancel");
    if (fits.length > 1)
        return schedule("schedule.which", fits);
    await removeBooking(storage, only.id);
    return schedule("schedule.cancelled", [only]);
}
/** The times both sides signed for, from this owner's jobs. */
async function signedJobs(storage) {
    return bookedTimes(await storage.list(Collection.jobs));
}
/** One message about the schedule in, one card out; `now` is the phone's own day and minute at `at`. */
export async function answerSchedule(text, storage, at, now) {
    const ask = readSchedule(text, now);
    log.debug("schedule: read", { action: ask.action, day: ask.day !== undefined, time: ask.start_min !== undefined });
    switch (ask.action) {
        case ScheduleAction.save:
            return save(storage, ask, at, now);
        case ScheduleAction.cancel:
            return cancel(storage, ask, now);
        case ScheduleAction.list:
            return list(storage, ask, now);
    }
}
//# sourceMappingURL=answer.js.map